import { useEffect, useState } from "react";
import { cashnator } from "../utils/functions";
import { PRIORITY_KEYS, PRIORITY_LABELS, PRODUCT_KEYS, PRODUCT_LABELS } from "./pricingMeta";

const fieldClass = "w-full rounded border border-primary/50 bg-black px-2 py-1 text-primary outline-none focus:border-primary";
const sectionClass = "rounded border border-primary/50 bg-mgs-bg p-4 text-left";
// Compacto de propósito: as duas tabelas precisam caber no painel sem scroll horizontal.
const cellClass = "px-1.5 py-1";
const headClass = "px-1.5 py-1 text-[10px] uppercase tracking-wide text-primary/70";

export const QUANTITY_STEPS = [1, 5, 10, 15, 20, 30, 40, 50, 60];

export const formatDiscountPercent = (subtotalCents, rawPriceCents) => {
    if (!rawPriceCents || rawPriceCents <= subtotalCents) {
        return "0%";
    }
    const percent = ((rawPriceCents - subtotalCents) / rawPriceCents) * 100;
    const rounded = Math.round(percent * 10) / 10;
    return `${String(rounded).replace(".", ",")}%`;
};

export const formatDeltaPercent = (subtotalCents, baseCents) => {
    if (!baseCents || subtotalCents === baseCents) {
        return "—";
    }
    const delta = ((subtotalCents - baseCents) / baseCents) * 100;
    const rounded = Math.round(delta * 10) / 10;
    return `${rounded > 0 ? "+" : ""}${String(rounded).replace(".", ",")}%`;
};

// Um lote só: primeiro um caso por prioridade ativa (tabela 1, quantidade atual),
// depois um caso por degrau de quantidade (tabela 2, prioridade selecionada).
export const buildComparativeCases = ({ gradingType, numberOfCards, autographEvaluation, activePriorities, selectedPriority, quantitySteps }) => {
    const priorityCases = activePriorities.map((priority) => ({
        gradingType,
        numberOfCards,
        priority,
        autographEvaluation,
    }));
    const quantityCases = selectedPriority
        ? quantitySteps.map((quantity) => ({
            gradingType,
            numberOfCards: quantity,
            priority: selectedPriority,
            autographEvaluation,
        }))
        : [];
    return { cases: [...priorityCases, ...quantityCases], prioritiesCount: priorityCases.length };
};

const perCard = (subtotalCents, numberOfCards) => {
    const cards = Number(numberOfCards);
    if (!cards || cards < 1) {
        return null;
    }
    return Math.round(subtotalCents / cards);
};

const PricingSimulator = ({ draft, simulation, runSimulation }) => {
    const activeProductKeys = PRODUCT_KEYS.filter((key) => draft?.products?.[key]?.active);
    const activePriorityKeys = PRIORITY_KEYS.filter((key) => draft?.priorities?.[key]?.active);

    const [gradingType, setGradingType] = useState(activeProductKeys[0] || "");
    // Sem input de quantidade: a quantidade da tabela de prioridades é escolhida
    // clicando numa linha da tabela de quantidades (degraus fixos).
    const [selectedQuantity, setSelectedQuantity] = useState(10);
    const [autographEvaluation, setAutographEvaluation] = useState(false);
    const [selectedPriority, setSelectedPriority] = useState(activePriorityKeys[0] || "");

    useEffect(() => {
        if (!activeProductKeys.includes(gradingType)) {
            setGradingType(activeProductKeys[0] || "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeProductKeys.join(",")]);

    useEffect(() => {
        if (!activePriorityKeys.includes(selectedPriority)) {
            setSelectedPriority(activePriorityKeys[0] || "");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activePriorityKeys.join(",")]);

    useEffect(() => {
        if (!gradingType || activePriorityKeys.length === 0) {
            return;
        }
        const { cases } = buildComparativeCases({
            gradingType,
            numberOfCards: selectedQuantity,
            autographEvaluation,
            activePriorities: activePriorityKeys,
            selectedPriority,
            quantitySteps: QUANTITY_STEPS,
        });
        runSimulation({ cases });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [gradingType, selectedQuantity, autographEvaluation, selectedPriority, activePriorityKeys.join(","), runSimulation]);

    const allResults = simulation?.status === "ready" ? simulation?.result?.results || [] : [];
    const priorityRows = allResults.slice(0, activePriorityKeys.length);
    const quantityRows = allResults.slice(activePriorityKeys.length);
    const baseSubtotal = priorityRows[0]?.subtotalCents;
    const selectedMaxItems = draft?.priorities?.[selectedPriority]?.maxItems;

    return (
        <section className={sectionClass}>
            <h2 className="mb-3 text-xl font-semibold text-[#d59106]">Simulador comparativo</h2>
            <p className="mb-3 text-xs text-primary/70">
                Valores em edição (antes de publicar) — clique numa prioridade e numa quantidade para comparar
            </p>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="text-sm font-semibold text-primary">
                    Produto
                    <select
                        className={fieldClass}
                        value={gradingType}
                        onChange={(event) => setGradingType(event.target.value)}
                    >
                        {activeProductKeys.length === 0 ? <option value="">Nenhum produto ativo</option> : null}
                        {activeProductKeys.map((key) => (
                            <option key={key} value={key}>{PRODUCT_LABELS[key] || key}</option>
                        ))}
                    </select>
                </label>

                <label className="flex items-end gap-2 pb-1.5 text-sm font-semibold text-primary">
                    <input
                        type="checkbox"
                        className="h-4 w-4 accent-[#d59106]"
                        checked={autographEvaluation}
                        onChange={(event) => setAutographEvaluation(event.target.checked)}
                    />
                    Autógrafo
                </label>
            </div>

            <div className="mt-4 rounded border border-dashed border-[#d59106]/40 p-2 text-primary">
                {simulation?.status === "loading" ? <p className="p-1 text-sm">Calculando...</p> : null}
                {simulation?.status === "error" ? (
                    <p className="p-1 text-sm text-red-300">{simulation?.result?.message || "Erro na simulação."}</p>
                ) : null}
                {simulation?.status === "idle" ? (
                    <p className="p-1 text-sm text-primary/60">Ajuste os campos para simular.</p>
                ) : null}

                {simulation?.status === "ready" && priorityRows.length > 0 ? (
                    <>
                        <p className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-[#d59106]">
                            Prioridades — {selectedQuantity} {selectedQuantity === 1 ? "carta" : "cartas"}
                        </p>
                        <table className="w-full table-fixed text-left text-xs">
                            <thead>
                                <tr className="border-b border-primary/30">
                                    <th className={`${headClass} w-[34%]`}>Prioridade</th>
                                    <th className={`${headClass} w-[22%]`}>Prazo</th>
                                    <th className={`${headClass} w-[26%]`}>Total</th>
                                    <th className={`${headClass} w-[18%]`}>vs. base</th>
                                </tr>
                            </thead>
                            <tbody>
                                {priorityRows.map((row) => (
                                    <tr
                                        key={row.priority}
                                        onClick={() => setSelectedPriority(row.priority)}
                                        className={`cursor-pointer border-b border-primary/10 hover:bg-[#d59106]/10 ${row.priority === selectedPriority ? "bg-[#d59106]/20" : ""}`}
                                        data-testid={`sim-row-${row.priority}`}
                                    >
                                        <td className={`${cellClass} truncate font-semibold`}>
                                            {row.priority === selectedPriority ? "▸ " : ""}
                                            {PRIORITY_LABELS[row.priority] || row.priority}
                                        </td>
                                        <td className={cellClass}>{row.deadlineDays}d úteis</td>
                                        <td className={`${cellClass} font-semibold`} title={`Desconto por volume: ${cashnator(Math.max(0, (row.rawPriceCents || 0) - row.subtotalCents))} (${formatDiscountPercent(row.subtotalCents, row.rawPriceCents)})`}>
                                            {cashnator(row.subtotalCents)}
                                        </td>
                                        <td className={cellClass}>{formatDeltaPercent(row.subtotalCents, baseSubtotal)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {quantityRows.length > 0 ? (
                            <div className="mt-3">
                                <p className="mb-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-[#d59106]">
                                    {PRIORITY_LABELS[selectedPriority] || selectedPriority} por quantidade
                                </p>
                                <table className="w-full table-fixed text-left text-xs" data-testid="sim-quantity-table">
                                    <thead>
                                        <tr className="border-b border-primary/30">
                                            <th className={`${headClass} w-[14%]`}>Cartas</th>
                                            <th className={`${headClass} w-[17%]`}>Prazo</th>
                                            <th className={`${headClass} w-[25%]`}>Total</th>
                                            <th className={`${headClass} w-[17%]`}>Desc.</th>
                                            <th className={`${headClass} w-[27%]`}>R$/carta</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {quantityRows.map((row) => {
                                            const acimaDoTeto = selectedMaxItems ? row.numberOfCards > selectedMaxItems : false;
                                            const selecionada = Number(row.numberOfCards) === Number(selectedQuantity);
                                            return (
                                                <tr
                                                    key={row.numberOfCards}
                                                    onClick={() => setSelectedQuantity(Number(row.numberOfCards))}
                                                    className={`cursor-pointer border-b border-primary/10 hover:bg-[#d59106]/10 ${selecionada ? "bg-[#d59106]/20" : ""} ${acimaDoTeto ? "opacity-40" : ""}`}
                                                    data-testid={`sim-qty-${row.numberOfCards}`}
                                                    title={acimaDoTeto ? `Acima do teto da fila (${selectedMaxItems} itens)` : undefined}
                                                >
                                                    <td className={`${cellClass} font-semibold`}>{selecionada ? "▸ " : ""}{row.numberOfCards}</td>
                                                    <td className={cellClass}>{row.deadlineDays}d úteis</td>
                                                    <td className={`${cellClass} font-semibold`}>{cashnator(row.subtotalCents)}</td>
                                                    <td className={cellClass} title={`Desconto por volume: ${cashnator(Math.max(0, (row.rawPriceCents || 0) - row.subtotalCents))}`}>
                                                        {formatDiscountPercent(row.subtotalCents, row.rawPriceCents)}
                                                    </td>
                                                    <td className={cellClass}>{cashnator(perCard(row.subtotalCents, row.numberOfCards) ?? 0)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : null}
                    </>
                ) : null}
            </div>
        </section>
    );
};

export default PricingSimulator;
